import { Controller } from "@hotwired/stimulus"
import { toast as toastService } from "../services/toast"

/**
 * Controlador para manejar productos escaneados en pestañas
 * Persiste los productos en localStorage para no perder el contexto
 * 
 * Uso:
 * <div data-controller="scanned-products">
 *   <div data-scanned-products-target="tabs"></div>
 *   <div data-scanned-products-target="content"></div>
 *   <span data-scanned-products-target="counter"></span>
 *   <div data-scanned-products-target="emptyState"></div>
 * </div>
 */
export default class extends Controller {
  static targets = ["tabs", "content", "counter", "emptyState", "clearButton"]
  static values = {
    storageKey: { type: String, default: "venta_scanned_products" }
  }

  connect() {
    this.products = this.loadFromStorage()
    this.activeProductId = this.products.length > 0 ? this.products[0].id : null
    this.render()
    
    // Escuchar eventos del scanner
    this.handleScanBound = this.handleScan.bind(this)
    document.addEventListener("scanner:detected", this.handleScanBound)
    
    // Escuchar cuando se crea un pedido exitosamente
    this.handleRequestCreatedBound = this.handleRequestCreated.bind(this)
    document.addEventListener("warehouse-request:created", this.handleRequestCreatedBound)
  }

  disconnect() {
    document.removeEventListener("scanner:detected", this.handleScanBound)
    document.removeEventListener("warehouse-request:created", this.handleRequestCreatedBound)
  }

  // Manejar código escaneado desde el scanner
  async handleScan(event) {
    const { code } = event.detail
    
    // Verificar si ya está en la lista
    const existing = this.products.find(p => 
      p.code === code || p.qrCode === code || p.barcode === code
    )
    
    if (existing) {
      this.setActiveProduct(existing.id)
      this.showToast("Producto ya escaneado", "info")
      return
    }

    // Buscar el inventario en el servidor
    await this.fetchAndAddProduct(code)
  }

  // Buscar producto por código y agregarlo
  async fetchAndAddProduct(code) {
    try {
      const response = await fetch(`/venta/inventario/scan.json?code=${encodeURIComponent(code)}`)
      
      if (response.ok) {
        const inventory = await response.json()
        this.addProduct(inventory)
      } else {
        this.showToast(`Producto no encontrado: ${code}`, "error")
      }
    } catch (error) {
      console.error("Error fetching product:", error)
      this.showToast("Error al buscar el producto", "error")
    }
  }

  // Agregar producto desde tarjeta de búsqueda (click en botón)
  addFromCard(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const button = event.currentTarget
    const inventoryData = JSON.parse(button.dataset.inventory)
    
    // Verificar si ya está
    const existing = this.products.find(p => p.id === inventoryData.id)
    if (existing) {
      this.setActiveProduct(existing.id)
      this.showToast("Producto ya agregado", "info")
      return
    }

    this.addProduct(inventoryData)
  }

  // Agregar producto a la lista
  addProduct(inventory) {
    // Mapear campos - el JSON del partial usa nombres directos
    const product = {
      id: inventory.id,
      code: inventory.code || inventory.product?.code,
      qrCode: inventory.qr_code || inventory.qrCode,
      barcode: inventory.barcode,
      description: inventory.web_description || inventory.product?.web_description || inventory.description,
      brand: inventory.brand || inventory.product?.brand?.name,
      color: inventory.color || inventory.product?.color?.name,
      size: inventory.size,
      quantity: inventory.quantity,
      price: inventory.price || inventory.product?.price,
      imageUrl: inventory.imageUrl || inventory.product?.image_thumb_url || null,
      addedAt: new Date().toISOString()
    }

    this.products.push(product)
    this.saveToStorage()
    this.setActiveProduct(product.id)
    this.render()
    this.showToast(`${product.code} - Talla ${product.size || 'U'} agregado`, "success")
  }

  // Remover producto de la lista
  removeProduct(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const productId = parseInt(event.currentTarget.dataset.productId)
    this.removeProductById(productId)
  }

  removeProductById(productId, { silent = false } = {}) {
    const index = this.products.findIndex(p => p.id === productId)
    
    if (index > -1) {
      const removed = this.products.splice(index, 1)[0]
      this.saveToStorage()
      
      // Si el producto activo fue removido, activar el siguiente o anterior
      if (this.activeProductId === productId) {
        if (this.products.length > 0) {
          // Activar el mismo índice o el último si era el último
          const newIndex = Math.min(index, this.products.length - 1)
          this.activeProductId = this.products[newIndex].id
        } else {
          this.activeProductId = null
        }
      }
      
      this.render()
      if (!silent) this.showToast(`${removed.code} removido`, "info")
    }
  }

  // Cambiar pestaña activa
  selectTab(event) {
    event.preventDefault()
    const productId = parseInt(event.currentTarget.dataset.productId)
    this.setActiveProduct(productId)
  }

  setActiveProduct(productId) {
    this.activeProductId = productId
    this.render()
    
    // Scroll a la pestaña activa si es necesario
    if (this.hasTabsTarget) {
      const activeTab = this.tabsTarget.querySelector(`[data-product-id="${productId}"]`)
      if (activeTab) {
        activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      }
    }
  }

  // Limpiar todos los productos
  clearAll(event) {
    event?.preventDefault()
    
    if (this.products.length === 0) return
    
    if (confirm("¿Eliminar todos los productos escaneados?")) {
      this.products = []
      this.activeProductId = null
      this.saveToStorage()
      this.render()
      this.showToast("Lista limpiada", "info")
    }
  }

  // Abrir modal para pedir a bodega
  async openWarehouseRequestModal(event) {
    event.preventDefault()
    const productId = parseInt(event.currentTarget.dataset.productId)
    const product = this.products.find(p => p.id === productId)
    
    if (!product) return

    await this.showWarehouseRequestModal(product)
  }

  async showWarehouseRequestModal(product) {
    try {
      const response = await fetch(`/venta/pedidos-bodega/modal_form?inventory_id=${product.id}`)
      
      if (!response.ok) throw new Error("Error loading form")
      
      const html = await response.text()
      
      // Crear el dialog
      const dialog = document.createElement("dialog")
      dialog.className = "catalog-dialog"
      dialog.id = "warehouse-request-modal"
      dialog.innerHTML = `
        <div class="dialog-content">
          <div class="dialog-header">
            <h3 class="dialog-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
              Pedir a Bodega
            </h3>
            <button type="button" class="dialog-close" aria-label="Cerrar">×</button>
          </div>
          <div class="dialog-body">
            ${html}
          </div>
        </div>
      `
      
      document.body.appendChild(dialog)
      
      // Manejar cierre
      const closeBtn = dialog.querySelector(".dialog-close")
      closeBtn.addEventListener("click", () => dialog.close())
      
      dialog.addEventListener("click", (e) => {
        if (e.target === dialog) dialog.close()
      })
      
      dialog.addEventListener("close", () => {
        document.body.classList.remove("modal-open")
        dialog.remove()
      })

      // Manejar envío del formulario
      const form = dialog.querySelector("form")
      if (form) {
        form.addEventListener("turbo:submit-end", (e) => {
          if (e.detail.success) {
            dialog.close()
          }
        })
      }
      
      dialog.showModal()
      document.body.classList.add("modal-open")
      
      // Focus en el primer campo
      const firstInput = dialog.querySelector("input:not([type='hidden']), select")
      if (firstInput) firstInput.focus()
      
    } catch (error) {
      console.error("Error loading modal:", error)
      this.showToast("Error al cargar el formulario", "error")
    }
  }

  // Manejar cuando un pedido se crea exitosamente (desde turbo_stream)
  handleRequestCreated(event) {
    const { inventoryId } = event.detail
    if (inventoryId) {
      this.removeProductById(parseInt(inventoryId), { silent: true })
    }
  }

  // Persistencia en localStorage
  loadFromStorage() {
    try {
      const data = localStorage.getItem(this.storageKeyValue)
      return data ? JSON.parse(data) : []
    } catch (e) {
      console.error("Error loading from storage:", e)
      return []
    }
  }

  saveToStorage() {
    try {
      localStorage.setItem(this.storageKeyValue, JSON.stringify(this.products))
    } catch (e) {
      console.error("Error saving to storage:", e)
    }
  }

  // Renderizar la UI
  render() {
    this.renderTabs()
    this.renderContent()
    this.renderCounter()
    this.toggleEmptyState()
    this.toggleClearButton()
  }

  renderTabs() {
    if (!this.hasTabsTarget) return

    if (this.products.length === 0) {
      this.tabsTarget.innerHTML = ""
      return
    }

    this.tabsTarget.innerHTML = this.products.map(product => `
      <div class="scanned-tab ${product.id === this.activeProductId ? 'active' : ''}"
           data-product-id="${product.id}">
        <div class="tab-content"
             data-action="click->scanned-products#selectTab"
             data-product-id="${product.id}">
          <span class="tab-code">${this.escapeHtml(product.code)}</span>
          <span class="tab-size">T. ${product.size || 'U'}</span>
        </div>
        <button type="button" 
                class="tab-close" 
                data-action="click->scanned-products#removeProduct"
                data-product-id="${product.id}"
                aria-label="Remover producto">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `).join("")
  }

  renderContent() {
    if (!this.hasContentTarget) return

    const product = this.products.find(p => p.id === this.activeProductId)
    
    if (!product) {
      this.contentTarget.innerHTML = ""
      this.contentTarget.classList.add("hidden")
      return
    }

    this.contentTarget.classList.remove("hidden")
    this.contentTarget.innerHTML = `
      <div class="scanned-product-detail">
        <div class="product-detail-main">
          <div class="product-detail-image">
            ${product.imageUrl 
              ? `<img src="${product.imageUrl}" alt="${this.escapeHtml(product.code)}" loading="lazy">`
              : `<div class="no-image-placeholder">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </div>`
            }
          </div>
          
          <div class="product-detail-info">
            <h4 class="product-code">${this.escapeHtml(product.code)}</h4>
            ${product.description ? `<p class="product-description">${this.escapeHtml(product.description)}</p>` : ''}
            <div class="product-meta-row">
              ${product.brand ? `<span class="meta-badge">${this.escapeHtml(product.brand)}</span>` : ''}
              ${product.color ? `<span class="meta-badge">${this.escapeHtml(product.color)}</span>` : ''}
            </div>
          </div>
        </div>
        
        <div class="product-detail-stats">
          <div class="stat-item">
            <span class="stat-label">Talla</span>
            <span class="stat-value size-value">${product.size || 'Única'}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Disponible</span>
            <span class="stat-value ${product.quantity > 5 ? 'stock-good' : (product.quantity > 0 ? 'stock-low' : 'stock-out')}">${product.quantity}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Precio</span>
            <span class="stat-value price-value">${this.formatCurrency(product.price)}</span>
          </div>
        </div>

        <div class="product-detail-actions">
          <button type="button" 
                  class="btn btn-primary"
                  data-action="click->scanned-products#openWarehouseRequestModal"
                  data-product-id="${product.id}">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            </svg>
            Pedir a Bodega
          </button>
          <button type="button" 
                  class="btn btn-outline-danger"
                  data-action="click->scanned-products#removeProduct"
                  data-product-id="${product.id}">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            Quitar
          </button>
        </div>
      </div>
    `
  }

  renderCounter() {
    if (!this.hasCounterTarget) return
    
    const count = this.products.length
    this.counterTarget.textContent = count
    this.counterTarget.classList.toggle("hidden", count === 0)
  }

  toggleEmptyState() {
    if (!this.hasEmptyStateTarget) return
    
    const hasProducts = this.products.length > 0
    this.emptyStateTarget.classList.toggle("hidden", hasProducts)
  }

  toggleClearButton() {
    if (!this.hasClearButtonTarget) return
    this.clearButtonTarget.classList.toggle("hidden", this.products.length === 0)
  }

  // Utilidades
  formatCurrency(amount) {
    if (!amount) return "$0.00"
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount)
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  showToast(message, type = "info") {
    toastService.show(type, message)
  }
}
